import React, { useCallback, useState, useEffect, useMemo, useRef } from "react";
import { Button } from "antd";
import Icon, { ExclamationCircleFilled, SyncOutlined } from "src/icons";
import { RouteComponentProps } from "react-router";
import ReactGA from "react-ga4";
import Tabs, { TabData } from "src/shared/components/Tabs";

import "./index.scss";
import TopNavbar, { MenuItem } from "src/shared/components/Navbar/NewNavbar";
import DetailsScroller from "src/shared/components/DetailsHorizontalScroll";
import CustomLineChart from "src/shared/components/LineChart";
import FiltersPane, { SelectedFilterValues } from "src/shared/components/FiltersPanev2";
import PinMetric from "src/shared/components/PinMetricModal";
import { REFRESH_ICON_ANIMATION, ThisWeekTabNames } from "src/shared/config";
import { TabNames } from "src/api/common";
import AccordianChart, { AccordianChartProps, DATA_UNIT } from "./AccordianChart";
import { DetailMetricType } from "src/shared/models/DetailsModel";
import { ReactComponent as CompareIcon } from "src/assets/chart-compare.svg";
import { useMetricsMetadata } from "src/shared/hooks";
import NSStatusHandler from "src/shared/components/NSStatusHandler";
import { truncate, safeGet, reduxOperationToStatus, mergeStatuses } from "src/shared/utils";

import {
  setFiltersFromL2Page,
  setFilters,
  fetchDetails,
  fetchNestedTreeData,
  clearDetails,
  addPin,
  setDimensions,
  filtersReset,
  FetchDetailsPayload,
  FetchDistFiltersPayload,
} from "src/store/details";
import { changeTimeTabAppWide, filtersConfigReset, setComingBackFromDetailsPage } from "src/store/app";
import { useRedux } from "src/shared/hooks";
import { IRootState } from "src/shared/types";
import { IReduxOperations, IActionFactory } from "src/store";
import ChainedDropdowns from "./ChainedDropdowns";
import { animated, useSpring } from "react-spring";
import styled from "@emotion/styled";
import colors from "src/styles/variables.scss";

const DEFAULT_COMPARE_NO = 2;

/**
 * Refer to wiki on bitbucket to understand filter logic
 * Metric type(metric vs pinned metric) is decided from the url
 * Different filter logic for both types of metrics
 * Note that once the page is rendered, and filters are populated, the filter can be changed
 */

const RefreshBadge = styled(animated.button)`
  border: none;
  border-radius: 16px;
  background: transparent;
  font-size: 12px;
  width: auto;
  line-height: 22px;
  padding: 0px 11px 0px 3px;
  display: flex;
  align-items: center;
  justify-self: end;
  margin-left: 20px;
  position: relative;
  outline: none;
  flex-direction: row-reverse;
  cursor: pointer;
  left: 9%;
  min-width: 126px;
  justify-content: flex-end;
  > span {
    /* margin-right: 10px; */
  }
`;

const IconHolder = styled.div`
  font-size: 16px;
  line-height: 13px;
`;

const CALL_DELAY = 1500;

type OptionsType = {
  className: string;
  iconClass: string;
  handleClick(): void;
};
type OptionGroupType = Array<OptionsType>;

type Tabtypes = {
  tabName: string;
}[];

const TOP_CHARTS = [
  { label: "NOW", valueField: "bigNumber", percentage: "vCompare", planPercentage: "vPlan" },
  { label: "CURRENT GOAL", valueField: "currentGoal" },
  { label: "BENCHMARK", valueField: "benchMark" },
];

interface IDetailsMappedProps {
  charts: IReduxOperations;
  filters: IObject<any>;
  l2Filters: IObject<any>;
  distributedFilters: IReduxOperations;
  selectedDimensions: Array<string>;
  selectedTab: string;
  filtersConfig: IReduxOperations;
}

/** TODO: Replace all the any types */
/** TODO: Remove all the unused Vars */
type MappedActionProps = {
  setFiltersFromL2Page();
  setFilters: IActionFactory<symbol, IObject<any>>;
  fetchDetails(payload: FetchDetailsPayload);
  fetchNestedTreeData(payload: FetchDistFiltersPayload);
  addPin(payload: string);
  clearDetails: IActionFactory<symbol, any>;
  setDimensions: IActionFactory<symbol, Array<string>>;
  filtersReset: IActionFactory<symbol, any>;
  filtersConfigReset: IActionFactory<symbol, any>;
  changeTimeTabAppWide: IActionFactory<symbol, string>;
  setComingBackFromDetailsPage: IActionFactory<symbol, boolean>;
};

const DetailsContainer: React.FC<RouteComponentProps<{ id: string; type: DetailMetricType }>> = ({
  history,
  match,
}) => {
  useEffect(() => {
    // ReactGA.pageview(window.location.pathname, [], "Details");
    ReactGA.send({ hitType: "pageview", page: window.location.pathname, title: "Details" });
  }, []);

  // FIXME: Catch if `metricOrPinId` not a number
  const { id: metricOrPinId, type: metricType } = match.params;
  const [showDialog, setShowDialog] = useState(false); // controls visibility of filter pane
  const [showModal, setShowModal] = useState(false); // controls visibility of add pin modal
  const [modalPause, setModalPause] = useState(false); // retain modal info if filter pane opened from the modal, reopen modal after filter is closed
  const [pauseData, setPauseData] = useState(""); // same as above, saves name field  const lineChartContainer = useRef<HTMLDivElement>(null);
  const lineChartContainer = useRef<HTMLDivElement>(null);
  const [isRefreshClicked, setIsRefreshClicked] = useState(0);
  const [metrics, metricsStatus] = useMetricsMetadata();

  const {
    setFiltersFromL2Page: setFiltersFromL2,
    filters,
    l2Filters,
    charts,
    selectedTab,
    setFilters: boundSetFilters,
    fetchDetails: fetchMetricDetails,
    distributedFilters,
    fetchNestedTreeData: fetchDistributedFilters,
    addPin: addNewPin,
    setDimensions: setDimensionsAction,
    selectedDimensions,
    filtersReset: filtersResetAction,
    changeTimeTabAppWide: changeTimeTab,
    setComingBackFromDetailsPage: toggleComingBackFromDetailsPage,
    // filtersConfigReset: filtersConfigResetAction,
    filtersConfig,
  } = useRedux<IRootState, IDetailsMappedProps, MappedActionProps>(
    (state) => ({
      filters: state.metricDetails.filters,
      l2Filters: state.category.filters,
      charts: state.metricDetails.charts,
      distributedFilters: state.metricDetails.distributedFilters,
      selectedTab: state.app.selectedTab,
      comingBackFromDetailsPage: state.app.comingBackFromDetailsPage,
      selectedDimensions: state.metricDetails.selectedDimensions,
      filtersConfig: state.app.filtersConfig,
    }),
    {
      setFiltersFromL2Page,
      setFilters,
      fetchDetails,
      fetchNestedTreeData,
      clearDetails,
      addPin,
      setDimensions,
      filtersReset,
      filtersConfigReset,
      changeTimeTabAppWide,
      setComingBackFromDetailsPage,
    }
  );

  const selectedDimensionsMap = useMemo<AccordianChartProps["selectedDimensions"]>(
    () =>
      new Map([
        ["FIRST_DIMENSION", selectedDimensions[0]],
        ["SECOND_DIMENSION", selectedDimensions[1]],
        ["THIRD_DIMENSION", selectedDimensions[2]],
      ]),
    [selectedDimensions]
  );

  /** Removing the selected dimensions on page navigation/component unmount(and not on refresh) */
  useEffect(() => {
    return () => {
      setDimensionsAction([]);
      filtersResetAction();
      filtersConfigReset();
    };
  }, []);

  useEffect(() => {
    // users are always pain in the a**
    // They might visit category page (L2) and then directly visit pin detail page by typing URL

    if (metricType !== DetailMetricType.PIN) {
      setFiltersFromL2();
    }
    fetchMetricDetails({
      metricId: metricOrPinId,
      filters: metricType === DetailMetricType.PIN ? undefined : l2Filters,
      metricType,
    });
  }, []);

  const onToggleDialog = useCallback(() => {
    setShowDialog(!showDialog);
    // setShowModal(false);
  }, [showDialog]);

  const badgeRef = useRef();
  const applyRef = (target) => {
    if (!badgeRef.current) {
      badgeRef.current = target && target.getBoundingClientRect();
    }
  };

  const onFilterInPinClick = useCallback(
    (e: React.MouseEvent) => {
      if (e) {
        const metricName = e.currentTarget.getAttribute("data-item") as string;
        setShowDialog(true);
        setShowModal(false);
        setModalPause(true);
        setPauseData(metricName);
      }
    },
    [showDialog]
  );

  const closeFilterClose = useCallback(() => {
    setShowDialog(!showDialog);
  }, [showDialog]);

  const onFiltersApply = useCallback((filters: SelectedFilterValues) => {
    setShowDialog(false);
    boundSetFilters(filters);
    filtersResetAction();
    fetchMetricDetails({ metricId: metricOrPinId, filters, metricType });
  }, []);

  const onRefresh = useCallback(() => {
    if (isRefreshClicked) return;
    setIsRefreshClicked(Date.now());

    setTimeout(() => {
      fetchMetricDetails({
        metricId: metricOrPinId,
        filters: filters,
        fresh: true,
        metricType,
      });
    }, CALL_DELAY);
  }, [filters, isRefreshClicked]);

  useEffect(() => {
    if (isRefreshClicked && Date.now() - isRefreshClicked > CALL_DELAY && safeGet(charts, "pending") === false) {
      setIsRefreshClicked(0);
    }
  }, [safeGet(charts, "pending"), isRefreshClicked]);

  const pinMetric = useCallback(() => {
    setShowModal(true);
  }, []);

  const handleChangeTab = useCallback(
    (tabData: TabData) => {
      changeTimeTab(tabData.key);
      fetchMetricDetails({ metricId: metricOrPinId, filters, metricType });
      filtersResetAction();
    },
    [filters, metricType, metricOrPinId]
  );

  const isFilterApplied = useMemo(
    () => Object.entries(filters).some(([key, value]: [string, any]): boolean => value.length !== 0),
    [filters]
  );

  const options: OptionGroupType = [
    {
      className: "option1",
      iconClass: "sync",
      handleClick: onRefresh,
    },
    {
      className: "option2",
      iconClass: "sliders",
      handleClick: onToggleDialog,
    },
    {
      className: "option3",
      iconClass: "pushpin",
      handleClick: pinMetric,
    },
  ];

  const onBack = useCallback(() => {
    toggleComingBackFromDetailsPage(true);
    history.goBack();
  }, [history]);

  const handleSubmitPin = useCallback(
    (metricName) => {
      const chartData = charts.payload;
      const newPin = {
        metricId: metricType === DetailMetricType.PIN ? safeGet(charts, "payload.metricId", 0) : metricOrPinId,
        label: metricName,
        filters: JSON.stringify(
          Object.entries(filters).map(([key, value]) => ({
            field: key,
            value: value,
          }))
        ),
        performanceChartIds: JSON.stringify([chartData.currentGoal, chartData.benchMark]),
        isPercentage: safeGet(charts, "payload.isPercentage", false),
        currentChartId: chartData.currentChartId,
        pastChartId: chartData.pastChartId,
        trendChartId: chartData.trendChartId,
        isPositive: chartData.isPositive,
      };
      setShowModal(false);
      addNewPin(JSON.stringify(newPin));
      setModalPause(false);
      setPauseData("");
    },
    [charts, filters]
  );

  const onModalClose = useCallback(() => {
    setShowModal(false);
    setModalPause(false);
    setPauseData("");
  }, []);

  const handleChainedDropdownChange = useCallback(
    (payload: string[]) => {
      setDimensionsAction(payload);
      if (!safeGet(payload, "0")) {
        filtersResetAction();
        return;
      }
      fetchDistributedFilters({
        tableChartId: safeGet(charts, "payload.tableChartId"),
        filters,
        selectedTab,
        firstSelectedValue: safeGet(payload, "0"),
        secondSelectedValue: safeGet(payload, "1"),
        lastSelectedValue: safeGet(payload, "2"),
        metricId: charts.payload.metricId, // FIXME: What if payload not yet preset
        ...(metricType === DetailMetricType.PIN && { pinId: charts.payload.id }),
      });
    },
    [setDimensionsAction, fetchDistributedFilters, charts, filters, selectedTab, metricOrPinId]
  );

  const compareThisMetricWith = (metricId?: number, sliceId?: number) => {
    const s = [{ metricId: charts.payload.metricId, sliceId: charts.payload.trendChartId, filters }];
    if (metricId) s.push({ metricId, sliceId, filters: {} });
    history.push("/metric-comparison", { compare: s });
  };

  const filterSliceId = filtersConfig.success
    ? (filtersConfig.payload || {}).config?.data?.meta?.queryParams?.form_data?.slice_id || 0
    : 0;
  const detailStatus = reduxOperationToStatus(charts);
  const filtersConfigStatus = reduxOperationToStatus(filtersConfig);

  const syncIcon = (
    <IconHolder>
      {safeGet(charts, "payload.isStaleMetric", false) && !isRefreshClicked ? (
        <ExclamationCircleFilled />
      ) : (
        <SyncOutlined />
      )}
    </IconHolder>
  );

  // ********** Animations Start ************

  const iconStyles = useSpring(REFRESH_ICON_ANIMATION);

  const [refreshBadgeStyles, refreshBadgeApi] = useSpring(() => {
    const isStale = safeGet(charts, "payload.isStaleMetric", false);
    return {
      duration: 300,
      translateX: 0,
      translateY: 0,
      opacity: isStale ? 1 : 0.6,
      backgroundColor: "transparent",
      color: isStale ? colors.anomalyIndicator : colors.paleYellow,
    };
  });

  const slideDownInitialState = { paddingTop: 0, duration: 300 };
  const [slideDownStyles, slideDownApi] = useSpring(() => slideDownInitialState);

  useEffect(() => {
    if (isRefreshClicked) {
      refreshBadgeApi({
        translateX:
          window.innerWidth / 2 - safeGet(badgeRef, "current.width", 0) / 2 - safeGet(badgeRef, "current.left", 0),
        translateY: 20,
        opacity: 1,
        backgroundColor: colors.canaryHighlight,
        color: colors.l2Background,
      });
      slideDownApi({
        paddingTop: 20,
      });
    } else {
      const isStale = safeGet(charts, "payload.isStaleMetric", false);
      refreshBadgeApi({
        translateX: 0,
        translateY: 0,
        opacity: isStale ? 1 : 0.6,
        backgroundColor: "transparent",
        color: isStale ? colors.anomalyIndicator : colors.paleYellow,
      });
      slideDownApi(slideDownInitialState);
    }
  }, [isRefreshClicked]);

  // ********** Animations End ************

  // Don't show previous metric name while data is being fetched for new one (Happens rarely)
  const metricName = metricOrPinId == charts.payload?.id ? charts.payload?.label : "";

  return (
    <>
      <div className="details-container">
        <>
          <TopNavbar isHome={false} handleClick={onBack} headerText={metricName} rootStyle={`padding-bottom: 0;`}>
            {options.map((option) => (
              <MenuItem
                key={option.iconClass}
                {...option}
                showMarker={option.iconClass == "sliders" && isFilterApplied}
              />
            ))}
          </TopNavbar>
          <RefreshBadge style={refreshBadgeStyles} onClick={onRefresh} ref={applyRef}>
            <span style={{ marginLeft: "5px" }}>
              updated {safeGet(charts, "payload.lastUpdatedTimeStamp") || " __:__"}
            </span>
            {safeGet(charts, "pending", false) ? <animated.div style={iconStyles}>{syncIcon}</animated.div> : syncIcon}
          </RefreshBadge>
        </>
        <animated.div style={slideDownStyles} className="details-body">
          <Tabs
            tabs={TabNames.map((tabName) => ({ title: tabName, key: tabName }))}
            page={selectedTab}
            onChange={handleChangeTab}
          >
            {TabNames.map((tabName) => {
              const isRenderLastHour = safeGet(charts, "payload.isRenderLastHour", false);
              const headerCards = isRenderLastHour
                ? tabName === "Today"
                  ? [
                      TOP_CHARTS[0],
                      {
                        label: "UNTIL LAST HOUR",
                        valueField: "lastHourBigNumber",
                        secondaryValueField: "lastHourCutoffLabel",
                        percentage: "lastHourVCompare",
                        progressField: "lastHourWowProgress",
                        planPercentage: "lastHourVPlan",
                        planProgressField: "lastHourPlanProgress",
                      },
                    ]
                  : [
                      TOP_CHARTS[0],
                      {
                        label: "UNTIL LAST HOUR",
                        valueField: "lastHourPlaceholder",
                      },
                    ]
                : TOP_CHARTS;

              return (
                <div key={tabName} className="detail-content">
                  {ThisWeekTabNames.includes(tabName) && safeGet(charts, "payload.isAnomalous", false) ? (
                  <p className="anomaly-text">
                    <ExclamationCircleFilled />
                    Deviation of this metric with respect to last week is higher than usual.
                  </p>
                ) : (
                  <p className="anomaly-text">
                    {safeGet(charts, "payload.isStaleMetric", false) && <ExclamationCircleFilled />}
                    {safeGet(charts, "payload.isStaleMetric", false)}
                  </p>
                )}

                <DetailsScroller data={headerCards} charts={charts.payload} loading={charts.pending} />
                <div className="custom-line-chart" ref={lineChartContainer}>
                  <div className="nested-main-content">
                    <div className="nested-label">TREND GRAPH</div>
                    <div className="nested-icon">{}</div>
                  </div>

                  <CustomLineChart
                    chart={safeGet(charts, "payload.trendChartData")}
                    isPending={safeGet(charts, "pending")}
                    width={safeGet(lineChartContainer, "current.clientWidth", 0)}
                    height={safeGet(lineChartContainer, "current.clientHeight", 0) * 0.85}
                    isPercentage={safeGet(charts, "payload.isPercentage", false)}
                  />
                  {charts.success && (
                    <div className="compare-with-container">
                      <NSStatusHandler status={metricsStatus}>
                        {() => (
                          <>
                            <Icon component={CompareIcon} />
                            <div className="label">COMPARE WITH</div>
                            {metrics
                              .filter((m) => m.metricId !== charts.payload.metricId)
                              .slice(0, DEFAULT_COMPARE_NO)
                              .map((m) => (
                                <Button
                                  key={m.id}
                                  size="small"
                                  type="link"
                                  style={{ textDecoration: "underline" }}
                                  onClick={() => compareThisMetricWith(m.metricId, m.trendChart)}
                                >
                                  {truncate(m.label, 16)}
                                </Button>
                              ))}
                            {metrics.slice(DEFAULT_COMPARE_NO).length > 0 && (
                              <Button size="small" type="link" onClick={() => compareThisMetricWith()}>
                                More...
                              </Button>
                            )}
                          </>
                        )}
                      </NSStatusHandler>
                    </div>
                  )}
                </div>

                <div className="nested-container">
                  <ChainedDropdowns
                    dropdownDatasources={safeGet(charts, "payload.aggregations", [])}
                    onChange={handleChainedDropdownChange}
                    value={selectedDimensions}
                  />
                  <AccordianChart
                    chartDataProps={distributedFilters}
                    selectedDimensions={selectedDimensionsMap}
                    isMetricDataLoading={safeGet(charts, "pending")}
                    onApplyFilter={onFiltersApply}
                    dataUnit={safeGet(charts, "payload.isPercentage", false) ? DATA_UNIT.PERCENTAGE : DATA_UNIT.DEFAULT}
                  />
                </div>

                <PinMetric
                  onModalClose={onModalClose}
                  handleSubmitPin={handleSubmitPin}
                  showModal={showModal || (modalPause && !showDialog)}
                  showFilter={onFilterInPinClick}
                  filters={filters}
                  isPending={safeGet(charts, "pending")}
                  title={modalPause ? pauseData : safeGet(charts, "payload.label", "")} // reopen pin modal after filter applied from modal
                />
              </div>
              );
            })}
          </Tabs>
        </animated.div>
      </div>
      {showDialog && charts.success && filtersConfig.success ? (
        <FiltersPane
          sliceId={filterSliceId} // user might click filter icon before loading of detail page
          onApply={onFiltersApply}
          initialValue={filters}
          aggregationFields={(charts.payload?.aggregations || []).filter(({ id }) => id !== "NULL")}
          callerStatus={mergeStatuses(detailStatus, filtersConfigStatus)}
          onClose={closeFilterClose}
        />
      ) : null}
    </>
  );
};

export default React.memo(DetailsContainer);
